import { useEffect } from 'react';
import { Link } from 'react-router-dom';

import DropdownMessage from './DropdownMessage';
import DropdownUser from './DropdownUser';
import DropdownNotification from './NotificationDropdown/DropdownNotification';

import LogoIcon from '../../images/logo/logo-icon.svg';

import DarkModeSwitcher from './DarkModeSwitcher';
import SearchInput from './SearchInput/SearchInput';

import useUiVisibility from '../../hooks/ui/useUiVisibility';

import SvgIcon from '../SvgIcon/SvgIcon';

interface HeaderProps {
  // =====================================================
  // 🔹 Mobile Overlay
  // =====================================================
  sidebarOpen: string | boolean | undefined;
  setSidebarOpen: (arg0: boolean) => void;

  // =====================================================
  // 🔹 Desktop Compact Mode
  // =====================================================
  sidebarExpanded?: boolean;
  setSidebarExpanded?: (expanded: boolean) => void;

  // =====================================================
  // 🔹 POS Mode
  // =====================================================
  compactMode?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  sidebarOpen,
  setSidebarOpen,

  sidebarExpanded,

  compactMode = false,
}) => {
  // El botón flotante de colapsar/expandir sidebar (SidebarDesktopToggle)
  // solo queda pegado al borde del contenido cuando el sidebar está
  // contraído (expandido, se mueve lejos, dentro del propio sidebar) — así
  // que el buscador solo necesita el margen extra en ese caso.
  const needsToggleClearance = !sidebarExpanded;
  // =====================================================
  // 📡 Configuración dinámica de visibilidad
  // =====================================================
  const { settings, fetchUiSettings } = useUiVisibility();

  useEffect(() => {
    fetchUiSettings();
  }, []);

  const isVisible = (code: string) =>
    settings.find((s) => s.code === code)?.visible ?? true;

  // =====================================================
  // ☰ Toggle Sidebar
  // =====================================================
  const handleSidebarToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    setSidebarOpen(!sidebarOpen);
  };

  // =====================================================
  // 🎨 Render
  // =====================================================
  return (
    <header className="sticky top-0 z-999 flex w-full bg-white drop-shadow-1 dark:bg-boxdark dark:drop-shadow-none">
      <div
        className={`
          flex w-full items-center justify-between shadow-2

          ${compactMode ? 'px-3 py-2' : 'px-4 py-4 md:px-6 2xl:px-11'}
        `}
      >
        {/* ===================================================== */}
        {/* 🔹 IZQUIERDA */}
        {/* ===================================================== */}
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {/* ☰ Botón Sidebar */}
          <button
            aria-controls="sidebar"
            onClick={handleSidebarToggle}
            className={`
            flex h-9 w-9 items-center justify-center

            rounded-lg

            text-body
            transition-all
            duration-200

            hover:bg-primary/10
            hover:text-primary

            dark:text-bodydark
            dark:hover:bg-primary/20
            dark:hover:text-primary

            lg:hidden
          `}
          >
            <SvgIcon name="expand-sidebar" width={18} height={18} />
          </button>

          {/* 🧩 Logo solo móvil */}
          <Link
            className={`
              flex-shrink-0
              ${compactMode ? 'hidden' : 'block lg:hidden'}
            `}
            to="/"
          >
            <img
              className="block h-auto w-9 dark:hidden"
              src={LogoIcon}
              alt="Logo claro"
            />

            <img
              className="hidden h-auto w-9 dark:block"
              src={LogoIcon}
              alt="Logo oscuro"
            />
          </Link>

          {/* ===================================================== */}
          {/* 🔍 SEARCH */}
          {/* ===================================================== */}
          {isVisible('SEARCH_BAR') && (
            <div
              className={`
                min-w-0

                ${compactMode ? 'max-w-2xl flex-1' : 'hidden sm:block'}
                ${needsToggleClearance ? (compactMode ? 'ml-8' : 'ml-10') : ''}
              `}
            >
              <SearchInput />
            </div>
          )}
        </div>

        {/* ===================================================== */}
        {/* 🔹 DERECHA */}
        {/* ===================================================== */}
        <div
          className={`
            flex items-center

            ${compactMode ? 'gap-3' : 'gap-5 2xsm:gap-7'}
          `}
        >
          <ul className="flex items-center gap-1.5 sm:gap-2">
            {/* 🌙 Dark Mode */}
            {isVisible('DARK_MODE_SWITCH') && <DarkModeSwitcher />}

            {/* 🔔 Notifications */}
            {!compactMode && isVisible('NOTIFICATIONS') && (
              <DropdownNotification />
            )}

            {/* 💬 Messages */}
            {!compactMode && isVisible('MESSAGES') && <DropdownMessage />}
          </ul>

          {/* 👤 Usuario */}
          <div className="flex items-center justify-end">
            <DropdownUser />
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
